import * as React from "react";
import {ReactNode, useState} from "react";
import {Link} from "react-router";
import {useIntl} from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronRight} from "@fortawesome/pro-regular-svg-icons";
import Sparkline from "../chart/Sparkline";
import {changeBetween} from "../../../helper/analysisChange";
import {PROVISIONAL_HATCH_STYLE} from "../chart/provisional";

export type PeriodValue = {
    amount: number
    isProvisional?: boolean
};

export type PeriodColumn = {
    key: string
    label: string
    isProvisional?: boolean // marks the whole column, e.g. an incomplete month
};

export type PeriodRow = {
    id: string
    label: ReactNode
    sparklineLabel?: string
    marker?: string // CSS color of a swatch before the label, e.g. to match a chart series
    values: PeriodValue[] // one per column, in the order of the columns
    total?: PeriodValue // sum of the values if not given
    children?: PeriodRow[]
};

type PeriodTableProps = {
    columns: PeriodColumn[]
    rows: PeriodRow[]
    totalRow?: PeriodRow
    labelHeader: string
    totalHeader: string
    formatAmount: (amount: number) => string
    rowLink?: (row: PeriodRow, parent?: PeriodRow) => string | undefined
    cellLink?: (row: PeriodRow, column: PeriodColumn, parent?: PeriodRow) => string | undefined
    showSparklines?: boolean
    selectedColumnKey?: string // highlights the column and shows the change to the column before in it
    onSelectColumn?: (key: string) => void // makes the column headers buttons
};

/**
 * A table of amounts per period, e.g. months: rows with their values in the period columns, a
 * total column and a sparkline next to the label. The sparkline leaves out provisional columns, so
 * a month with missing data does not read as a drop. Rows with children are groups that can be
 * expanded. Provisional values are italic and hatched. The label column stays in place when the
 * table is scrolled horizontally.
 *
 * If a column is selected, its cells are highlighted and show the change to the column before
 * (absolute and in percent), so the table explains the figures of the selected period. Clicking a
 * column header selects the column, if onSelectColumn is given.
 *
 * Labels and cells become links if rowLink and cellLink return a path for them. Cells with zero
 * are never linked, as they would lead to an empty list.
 */
const PeriodTable: React.FC<PeriodTableProps> = ({columns, rows, totalRow, labelHeader, totalHeader, formatAmount, rowLink, cellLink, showSparklines = true, selectedColumnKey, onSelectColumn}) => {
    const intl = useIntl();
    const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());

    const toggle = (rowId: string) => {
        setExpandedRowIds(previous => {
            const next = new Set(previous);
            if (next.has(rowId)) {
                next.delete(rowId);
            } else {
                next.add(rowId);
            }
            return next;
        });
    };

    const selectedColumnIndex = columns.findIndex(column => column.key === selectedColumnKey);

    const renderChange = (value: PeriodValue, previous: PeriodValue | undefined) => {
        if (previous === undefined || (value.amount === 0 && previous.amount === 0)) {
            return null;
        }
        const {delta, percent} = changeBetween(value.amount, previous.amount);
        const deltaLabel = (delta > 0 ? '+' : '') + formatAmount(delta);
        const percentLabel = percent === null ? '' : ' · ' + intl.formatNumber(percent, {style: 'percent', maximumFractionDigits: 1, signDisplay: 'exceptZero'});
        return (
            <span className="block text-[11px] font-normal not-italic text-gray-500" title={intl.formatMessage({id: 'component.periodTable.changeToPrevious'})}>
                {deltaLabel}{percentLabel}
            </span>
        );
    };

    const renderAmountCell = (value: PeriodValue, key: string, link: string | undefined, background: string, isColumnProvisional = false, change: ReactNode = null, isSelected = false) => {
        const isProvisional = value.isProvisional === true || isColumnProvisional;
        const content = value.amount === 0
            ? <span className="text-gray-300">–</span>
            : formatAmount(value.amount);
        const body = <>{content}{change}</>;
        return (
            <td key={key} className={`p-0 text-right whitespace-nowrap ${isSelected ? 'bg-blue-50' : background} ${isProvisional ? 'italic text-gray-500' : ''}`} style={isProvisional ? PROVISIONAL_HATCH_STYLE : undefined}>
                {link !== undefined && value.amount !== 0
                    ? <Link to={link} className="block px-2.5 py-2 hover:underline hover:text-blue-700">{body}</Link>
                    : <span className="block px-2.5 py-2">{body}</span>}
            </td>
        );
    };

    const renderRow = (row: PeriodRow, kind: 'group' | 'child' | 'total', parent?: PeriodRow) => {
        const hasChildren = (row.children?.length ?? 0) > 0;
        const isExpanded = expandedRowIds.has(row.id);
        const background = kind === 'child' ? 'bg-gray-50 group-hover:bg-gray-100' : kind === 'total' ? 'bg-white' : 'bg-white group-hover:bg-gray-100';
        const total = row.total ?? {
            amount: row.values.reduce((sum, value) => sum + value.amount, 0),
            isProvisional: row.values.some(value => value.isProvisional),
        };
        const labelLink = kind !== 'total' ? rowLink?.(row, parent) : undefined;
        const fontWeight = kind === 'total' ? 'font-semibold' : kind === 'group' ? 'font-medium' : 'font-normal';

        return (
            <tr key={(parent ? parent.id + '/' : '') + row.id} className={`group ${fontWeight}`}>
                <th scope="row" className={`sticky left-0 z-10 p-0 text-left ${fontWeight} border-r border-gray-200 ${background}`}>
                    <div className={`flex items-center gap-2 py-2 pr-3 w-72 ${kind === 'child' ? 'pl-10' : 'pl-3'}`}>
                        {kind === 'group' && (hasChildren
                            ? <button
                                type="button"
                                onClick={() => toggle(row.id)}
                                aria-expanded={isExpanded}
                                title={intl.formatMessage({id: isExpanded ? 'component.periodTable.collapse' : 'component.periodTable.expand'})}
                                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                            >
                                <FontAwesomeIcon icon={faChevronRight} className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}/>
                            </button>
                            : <span className="w-5 shrink-0"/>)}
                        {row.marker && <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{backgroundColor: row.marker}}/>}
                        <span className="min-w-0 flex-1 truncate" title={typeof row.label === 'string' ? row.label : undefined}>
                            {labelLink !== undefined
                                ? <Link to={labelLink} className="hover:underline hover:text-blue-700">{row.label}</Link>
                                : row.label}
                        </span>
                        {showSparklines && kind !== 'total' && (
                            <span className="shrink-0">
                                <Sparkline points={row.values.filter((_value, index) => !columns[index]?.isProvisional).map(value => ({value: value.amount, isProvisional: value.isProvisional}))} label={row.sparklineLabel}/>
                            </span>
                        )}
                    </div>
                </th>
                {row.values.map((value, index) => renderAmountCell(
                    value,
                    columns[index]?.key ?? String(index),
                    kind !== 'total' && columns[index] ? cellLink?.(row, columns[index], parent) : undefined,
                    background,
                    columns[index]?.isProvisional,
                    index === selectedColumnIndex ? renderChange(value, row.values[index - 1]) : null,
                    index === selectedColumnIndex,
                ))}
                {renderAmountCell(total, 'total', labelLink, `${background} border-l border-gray-200 font-semibold`)}
            </tr>
        );
    };

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-0 text-sm tabular-nums text-gray-900">
                <thead>
                    <tr>
                        <th scope="col" className="sticky left-0 z-10 bg-white border-b border-r border-gray-200 px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{labelHeader}</th>
                        {columns.map(column => (
                            <th
                                key={column.key}
                                scope="col"
                                aria-sort={column.key === selectedColumnKey ? 'descending' : undefined}
                                className={`border-b px-2.5 py-2 text-right text-xs whitespace-nowrap ${column.key === selectedColumnKey ? 'bg-blue-50 border-blue-600 font-semibold text-blue-700' : 'bg-white border-gray-200 font-medium text-gray-500'} ${column.isProvisional ? 'italic' : ''}`}
                                style={column.isProvisional && column.key !== selectedColumnKey ? PROVISIONAL_HATCH_STYLE : undefined}
                            >
                                {onSelectColumn
                                    ? <button type="button" onClick={() => onSelectColumn(column.key)} className="hover:text-blue-700 hover:underline">{column.label}</button>
                                    : column.label}
                            </th>
                        ))}
                        <th scope="col" className="bg-white border-b border-l border-gray-200 px-3 py-2 text-right text-xs font-medium uppercase tracking-wide whitespace-nowrap text-gray-500">{totalHeader}</th>
                    </tr>
                </thead>
                <tbody className="[&>tr>*]:border-b [&>tr>*]:border-gray-100">
                    {rows.flatMap(row => [
                        renderRow(row, 'group'),
                        ...(expandedRowIds.has(row.id) ? (row.children ?? []).map(child => renderRow(child, 'child', row)) : []),
                    ])}
                </tbody>
                {totalRow && (
                    <tfoot className="[&>tr>*]:border-t [&>tr>*]:border-gray-300">
                        {renderRow(totalRow, 'total')}
                    </tfoot>
                )}
            </table>
        </div>
    );
};

export default PeriodTable;
